import { useState, useEffect, useCallback } from 'react';
import AppHeader from '@/components/AppHeader';
import {
  getTournaments,
  getMatchesByTournament,
  getTeams,
  getRalliesBySet,
  updateRally,
  addRallyToSet,
  recomputeSetScoresFromRallies,
} from '@/lib/supabase';
import {
  LostReason,
  LOST_REASONS,
  LOST_REASON_LABELS,
  LOST_REASON_REQUIRES_WINNER,
  Tournament,
} from '@/types';

interface Player {
  id: string;
  name: string;
  teamId: string;
}

interface TeamInfo {
  id: string;
  name: string;
  shortName: string;
  color: string;
  players: Player[];
}

interface RallyRow {
  id: string;
  rally_number: number;
  serving_team_id: string;
  server_id: string;
  point_won_by_id: string;
  lost_reason: string | null;
  point_winner_id: string | null;
}

interface SetRow {
  id: string;
  set_number: number;
  score_a: number;
  score_b: number;
  ended_at: string | null;
}

interface MatchRow {
  id: string;
  match_number: number;
  status: string;
  date: string | null;
  team_a_id: string;
  team_b_id: string;
  sets: SetRow[];
}

interface RallyDraft {
  pointWonById: string;
  serverId: string;
  lostReason: LostReason | '';
  pointWinnerId: string;
}

const emptyDraft = (): RallyDraft => ({ pointWonById: '', serverId: '', lostReason: '', pointWinnerId: '' });

interface Props {
  onSignOut: () => void;
}

export default function RallyEditor({ onSignOut }: Props) {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [matches, setMatches] = useState<MatchRow[]>([]);
  const [sets, setSets] = useState<SetRow[]>([]);
  const [rallies, setRallies] = useState<RallyRow[]>([]);
  const [teamA, setTeamA] = useState<TeamInfo | null>(null);
  const [teamB, setTeamB] = useState<TeamInfo | null>(null);
  const [allTeamsMap, setAllTeamsMap] = useState<Map<string, { shortName: string; color: string }>>(new Map());

  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [selectedMatchId, setSelectedMatchId] = useState('');
  const [selectedSetId, setSelectedSetId] = useState('');

  const [loadingMatches, setLoadingMatches] = useState(false);
  const [loadingRallies, setLoadingRallies] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<RallyDraft>(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  // Add Rally state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addDraft, setAddDraft] = useState<RallyDraft>(emptyDraft());
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    getTournaments().then(setTournaments).catch(() => setTournaments([]));
    getTeams().then(all => {
      const map = new Map<string, { shortName: string; color: string }>();
      all.forEach((t: any) => map.set(t.id, { shortName: t.short_name, color: t.color }));
      setAllTeamsMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedTournamentId) { setMatches([]); setSelectedMatchId(''); return; }
    setLoadingMatches(true);
    getMatchesByTournament(selectedTournamentId)
      .then(data => setMatches(data as MatchRow[]))
      .catch(() => setMatches([]))
      .finally(() => setLoadingMatches(false));
  }, [selectedTournamentId]);

  useEffect(() => {
    if (!selectedMatchId) { setSets([]); setSelectedSetId(''); setTeamA(null); setTeamB(null); return; }
    const match = matches.find(m => m.id === selectedMatchId);
    if (!match) return;
    setSets(match.sets ?? []);
    setSelectedSetId('');
    getTeams().then(allTeams => {
      const a = allTeams.find((t: any) => t.id === match.team_a_id);
      const b = allTeams.find((t: any) => t.id === match.team_b_id);
      setTeamA(a ? {
        id: a.id, name: a.name, shortName: a.short_name, color: a.color,
        players: a.players.map((p: any) => ({ id: p.id, name: p.name, teamId: p.team_id })),
      } : null);
      setTeamB(b ? {
        id: b.id, name: b.name, shortName: b.short_name, color: b.color,
        players: b.players.map((p: any) => ({ id: p.id, name: p.name, teamId: p.team_id })),
      } : null);
    }).catch(() => {});
  }, [selectedMatchId, matches]);

  useEffect(() => {
    if (!selectedSetId) { setRallies([]); return; }
    setLoadingRallies(true);
    getRalliesBySet(selectedSetId)
      .then(data => setRallies((data as RallyRow[]).slice().reverse()))
      .catch(() => setRallies([]))
      .finally(() => setLoadingRallies(false));
  }, [selectedSetId]);

  const allPlayers: Player[] = [...(teamA?.players ?? []), ...(teamB?.players ?? [])];
  const teamOf = useCallback((teamId: string) => teamA?.id === teamId ? teamA : teamB, [teamA, teamB]);
  const playerName = useCallback((playerId: string | null) =>
    allPlayers.find(p => p.id === playerId)?.name ?? '—', [allPlayers]);

  const openEdit = (rally: RallyRow) => {
    setEditingId(rally.id);
    setSaveError(null);
    setSavedId(null);
    setDraft({
      pointWonById: rally.point_won_by_id,
      serverId: rally.server_id,
      lostReason: (rally.lost_reason as LostReason) || '',
      pointWinnerId: rally.point_winner_id ?? '',
    });
  };

  const cancelEdit = () => { setEditingId(null); setSaveError(null); };

  const handleSave = async (rally: RallyRow) => {
    const match = matches.find(m => m.id === selectedMatchId);
    if (!match) return;
    setSaving(true);
    setSaveError(null);
    const lostReason = draft.lostReason || null;
    const pointWinnerId = (lostReason && LOST_REASON_REQUIRES_WINNER[lostReason as LostReason])
      ? (draft.pointWinnerId || null) : null;
    const serverPlayer = allPlayers.find(p => p.id === draft.serverId);
    const servingTeamId = serverPlayer?.teamId ?? rally.serving_team_id;

    try {
      await updateRally(rally.id, {
        pointWonById: draft.pointWonById,
        serverId: draft.serverId || undefined,
        servingTeamId: draft.serverId ? servingTeamId : undefined,
        lostReason,
        pointWinnerId,
      });
      const updatedScores = await recomputeSetScoresFromRallies(selectedSetId, match.team_a_id, match.team_b_id);
      setRallies(prev => prev.map(r =>
        r.id === rally.id
          ? { ...r, point_won_by_id: draft.pointWonById, server_id: draft.serverId || r.server_id, serving_team_id: servingTeamId, lost_reason: lostReason, point_winner_id: pointWinnerId }
          : r
      ));
      setSets(prev => prev.map(s =>
        s.id === selectedSetId ? { ...s, score_a: updatedScores.scoreA, score_b: updatedScores.scoreB } : s
      ));
      setSavedId(rally.id);
      setEditingId(null);
    } catch (err: any) {
      setSaveError(err?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRally = async () => {
    if (!addDraft.pointWonById || !addDraft.serverId) {
      setAddError('Winner and server are required.');
      return;
    }
    const match = matches.find(m => m.id === selectedMatchId);
    if (!match) return;
    const serverPlayer = allPlayers.find(p => p.id === addDraft.serverId);
    const servingTeamId = serverPlayer?.teamId ?? addDraft.pointWonById;
    const lostReason = addDraft.lostReason || null;
    const pointWinnerId = (lostReason && LOST_REASON_REQUIRES_WINNER[lostReason as LostReason])
      ? (addDraft.pointWinnerId || null) : null;

    setAddSaving(true);
    setAddError(null);
    try {
      const newRally = await addRallyToSet({
        setId: selectedSetId,
        servingTeamId,
        serverId: addDraft.serverId,
        pointWonById: addDraft.pointWonById,
        lostReason,
        pointWinnerId,
      });
      const updatedScores = await recomputeSetScoresFromRallies(selectedSetId, match.team_a_id, match.team_b_id);
      setRallies(prev => [
        {
          id: newRally.id,
          rally_number: newRally.rally_number,
          serving_team_id: servingTeamId,
          server_id: addDraft.serverId,
          point_won_by_id: addDraft.pointWonById,
          lost_reason: lostReason,
          point_winner_id: pointWinnerId,
        },
        ...prev,
      ]);
      setSets(prev => prev.map(s =>
        s.id === selectedSetId ? { ...s, score_a: updatedScores.scoreA, score_b: updatedScores.scoreB } : s
      ));
      setShowAddForm(false);
      setAddDraft(emptyDraft());
    } catch (err: any) {
      setAddError(err?.message ?? 'Failed to add rally');
    } finally {
      setAddSaving(false);
    }
  };

  // Running score (rallies displayed newest-first, so reverse for calc)
  const runningScores = (() => {
    let a = 0, b = 0;
    return rallies.map(r => {
      if (r.point_won_by_id === teamA?.id) a++;
      else if (r.point_won_by_id === teamB?.id) b++;
      return { a, b };
    });
  })();

  const selectedSet = sets.find(s => s.id === selectedSetId);

  const PlayerSelects = ({ teamAPlayers, teamBPlayers, value, onChange }: {
    teamAPlayers: Player[]; teamBPlayers: Player[];
    value: string; onChange: (v: string) => void;
  }) => (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full text-sm py-2.5 px-3 rounded-lg bg-surface-raised border border-border text-text-primary focus:outline-none focus:border-primary">
      <option value="">— select player —</option>
      {teamA && <optgroup label={teamA.name}>{teamAPlayers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>}
      {teamB && <optgroup label={teamB.name}>{teamBPlayers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>}
    </select>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader onSignOut={onSignOut} title="Rally Editor" navIcon="admin" />

      <div className="flex-1 overflow-y-auto px-4 py-4 max-w-2xl mx-auto w-full">

        {/* Selectors */}
        <div className="card mb-4 space-y-3">
          <h2 className="text-xs font-bold text-text-muted uppercase tracking-wider">Select Match</h2>

          <div>
            <label className="text-xs text-text-muted mb-1 block">Tournament</label>
            <select value={selectedTournamentId}
              onChange={e => { setSelectedTournamentId(e.target.value); setSelectedMatchId(''); setSelectedSetId(''); }}
              className="input-field text-sm">
              <option value="">— pick a tournament —</option>
              {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          {selectedTournamentId && (
            <div>
              <label className="text-xs text-text-muted mb-1 block">Match</label>
              {loadingMatches ? <p className="text-xs text-text-muted">Loading…</p> : (
                <select value={selectedMatchId}
                  onChange={e => { setSelectedMatchId(e.target.value); setSelectedSetId(''); }}
                  className="input-field text-sm">
                  <option value="">— pick a match —</option>
                  {matches.map(m => {
                    const a = allTeamsMap.get(m.team_a_id);
                    const b = allTeamsMap.get(m.team_b_id);
                    const vs = a && b ? `${a.shortName} vs ${b.shortName}` : '';
                    const dateStr = m.date
                      ? new Date(m.date + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
                      : '';
                    return (
                      <option key={m.id} value={m.id}>
                        #{m.match_number}{vs ? ` — ${vs}` : ''}{dateStr ? ` · ${dateStr}` : ''} ({m.status})
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
          )}

          {selectedMatchId && teamA && teamB && (
            <div>
              <label className="text-xs text-text-muted mb-1 block">Set</label>
              <select value={selectedSetId} onChange={e => setSelectedSetId(e.target.value)} className="input-field text-sm">
                <option value="">— pick a set —</option>
                {sets.map(s => (
                  <option key={s.id} value={s.id}>
                    Set {s.set_number}{s.ended_at ? ` (${s.score_a}–${s.score_b})` : ' — in progress'}
                  </option>
                ))}
              </select>
              <div className="flex gap-3 mt-1.5">
                <span className="text-xs font-semibold" style={{ color: teamA.color }}>{teamA.shortName}</span>
                <span className="text-xs text-text-muted">vs</span>
                <span className="text-xs font-semibold" style={{ color: teamB.color }}>{teamB.shortName}</span>
              </div>
            </div>
          )}
        </div>

        {/* Rally list */}
        {selectedSetId && (
          <>
            {loadingRallies ? (
              <div className="text-center py-10 text-text-muted text-sm">Loading rallies…</div>
            ) : rallies.length === 0 ? (
              <div className="text-center py-10 text-text-muted text-sm">No rallies found.</div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="text-xs font-bold text-text-muted uppercase tracking-wider">
                    {rallies.length} Rallies — Set {selectedSet?.set_number}
                  </h2>
                  {savedId && <span className="text-xs text-success font-semibold">✓ Saved</span>}
                </div>

                {rallies.map((rally, idx) => {
                  const servingTeam = teamOf(rally.serving_team_id);
                  const winnerTeam = teamOf(rally.point_won_by_id);
                  const score = runningScores[idx];
                  const isEditing = editingId === rally.id;
                  const justSaved = savedId === rally.id && !isEditing;
                  const lostReason = rally.lost_reason as LostReason | null;

                  return (
                    <div key={rally.id} className={`rounded-lg border transition-all ${
                      isEditing ? 'border-primary bg-primary/5' :
                      justSaved ? 'border-success/50 bg-success/5' : 'border-border bg-surface'}`}>

                      {/* Summary row */}
                      <div className="px-3 py-2.5 flex items-center gap-2">
                        <span className="text-xs text-text-muted font-mono w-6 flex-shrink-0">#{rally.rally_number}</span>
                        <span className="font-mono text-xs text-text-secondary flex-shrink-0 w-10 text-center">{score.a}–{score.b}</span>
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="text-xs font-bold flex-shrink-0" style={{ color: servingTeam?.color }}>{servingTeam?.shortName}</span>
                          <span className="text-xs text-text-muted truncate max-w-[70px]">{playerName(rally.server_id)}</span>
                        </div>
                        <span className="text-text-muted text-xs flex-shrink-0">→</span>
                        <span className="text-xs font-bold flex-shrink-0" style={{ color: winnerTeam?.color }}>{winnerTeam?.shortName}</span>
                        {lostReason && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-surface-raised text-text-muted flex-shrink-0">
                            {LOST_REASON_LABELS[lostReason]}
                          </span>
                        )}
                        {rally.point_winner_id && (
                          <span className="text-[10px] text-text-muted truncate flex-shrink-0 max-w-[60px]">
                            {playerName(rally.point_winner_id)}
                          </span>
                        )}
                        <div className="flex-1" />
                        {!isEditing && (
                          <button onClick={() => openEdit(rally)}
                            className="text-sm text-primary font-bold flex-shrink-0 py-1.5 px-4 rounded-lg border border-primary/40 hover:border-primary active:scale-95 transition-all">
                            Edit
                          </button>
                        )}
                      </div>

                      {/* Editor */}
                      {isEditing && (
                        <div className="px-3 pb-4 pt-2 border-t border-primary/20 space-y-4">

                          {/* Point won by */}
                          <div>
                            <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Point won by</p>
                            <div className="grid grid-cols-2 gap-2">
                              {[teamA, teamB].filter(Boolean).map(team => (
                                <button key={team!.id}
                                  onClick={() => setDraft(d => ({ ...d, pointWonById: team!.id }))}
                                  className={`py-3 rounded-lg border-2 font-bold text-sm transition-all active:scale-[0.98] ${draft.pointWonById === team!.id ? 'text-white' : 'bg-surface border-border text-text-secondary'}`}
                                  style={draft.pointWonById === team!.id ? { backgroundColor: team!.color, borderColor: team!.color } : undefined}>
                                  {team!.shortName}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Server */}
                          <div>
                            <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Server</p>
                            <PlayerSelects teamAPlayers={teamA?.players ?? []} teamBPlayers={teamB?.players ?? []}
                              value={draft.serverId} onChange={v => setDraft(d => ({ ...d, serverId: v }))} />
                          </div>

                          {/* Lost reason */}
                          <div>
                            <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Lost reason</p>
                            {/* Serve Foul shortcut — auto-flips winner to opponent of server */}
                            <button
                              onClick={() => {
                                const serverPlayer = allPlayers.find(p => p.id === draft.serverId);
                                const opponentId = serverPlayer?.teamId === teamA?.id ? teamB?.id : teamA?.id;
                                setDraft(d => ({ ...d, lostReason: 'FOUL', pointWonById: opponentId ?? d.pointWonById, pointWinnerId: '' }));
                              }}
                              className={`w-full mb-2 py-2.5 rounded-lg border-2 font-bold text-xs transition-all active:scale-[0.98] ${draft.lostReason === 'FOUL' ? 'bg-error border-error text-white' : 'border-error/40 text-error hover:border-error'}`}
                            >
                              SERVE FOUL — opponent gets point
                            </button>
                            <div className="grid grid-cols-3 gap-2">
                              {LOST_REASONS.map(reason => (
                                <button key={reason}
                                  onClick={() => setDraft(d => ({ ...d, lostReason: d.lostReason === reason ? '' : reason, pointWinnerId: '' }))}
                                  className={`py-2.5 rounded-lg border font-bold text-xs transition-all active:scale-[0.98] ${draft.lostReason === reason ? 'bg-primary border-primary text-white' : 'bg-surface border-border text-text-secondary'}`}>
                                  {LOST_REASON_LABELS[reason]}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Point winner — IN / TOUCH only */}
                          {draft.lostReason && LOST_REASON_REQUIRES_WINNER[draft.lostReason as LostReason] && (
                            <div>
                              <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Point winner player</p>
                              <PlayerSelects teamAPlayers={teamA?.players ?? []} teamBPlayers={teamB?.players ?? []}
                                value={draft.pointWinnerId} onChange={v => setDraft(d => ({ ...d, pointWinnerId: v }))} />
                            </div>
                          )}

                          {saveError && <p className="text-sm text-error font-semibold">{saveError}</p>}

                          <div className="flex gap-2 pt-1">
                            <button onClick={() => handleSave(rally)} disabled={saving || !draft.pointWonById}
                              className="flex-1 py-3 rounded-lg font-bold text-sm bg-primary text-white hover:opacity-90 disabled:opacity-50 active:scale-[0.98] transition-all">
                              {saving ? 'Saving…' : 'Save Changes'}
                            </button>
                            <button onClick={cancelEdit} disabled={saving}
                              className="py-3 px-5 rounded-lg font-semibold text-sm border border-border text-text-secondary hover:bg-surface-raised active:scale-[0.98] transition-all">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Rally */}
            {teamA && teamB && (
              <div className="mt-4">
                {!showAddForm ? (
                  <button onClick={() => { setShowAddForm(true); setAddError(null); }}
                    className="w-full py-3 rounded-lg border-2 border-dashed border-border text-text-muted hover:border-primary hover:text-primary font-semibold text-sm transition-all active:scale-[0.98]">
                    + Add Rally
                  </button>
                ) : (
                  <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-4 space-y-4">
                    <p className="text-xs font-bold text-primary uppercase tracking-wider">
                      Add Rally #{(rallies[0]?.rally_number ?? 0) + 1}
                    </p>

                    {/* Winner */}
                    <div>
                      <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Point won by</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[teamA, teamB].map(t => (
                          <button key={t.id} onClick={() => setAddDraft(d => ({ ...d, pointWonById: t.id }))}
                            className={`py-3 rounded-lg border-2 font-bold text-sm transition-all active:scale-95 ${addDraft.pointWonById === t.id ? 'text-white' : 'bg-surface-raised text-text-primary border-border'}`}
                            style={addDraft.pointWonById === t.id ? { backgroundColor: t.color, borderColor: t.color } : {}}>
                            {t.shortName}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Server */}
                    <div>
                      <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Server</p>
                      <PlayerSelects teamAPlayers={teamA.players} teamBPlayers={teamB.players}
                        value={addDraft.serverId} onChange={v => setAddDraft(d => ({ ...d, serverId: v }))} />
                    </div>

                    {/* Lost reason */}
                    <div>
                      <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Lost reason (optional)</p>
                      {/* Serve Foul shortcut — auto-flips winner to opponent of server */}
                      <button
                        onClick={() => {
                          const serverPlayer = allPlayers.find(p => p.id === addDraft.serverId);
                          const opponentId = serverPlayer?.teamId === teamA.id ? teamB.id : teamA.id;
                          setAddDraft(d => ({ ...d, lostReason: 'FOUL', pointWonById: opponentId, pointWinnerId: '' }));
                        }}
                        className={`w-full mb-2 py-2.5 rounded-lg border-2 font-bold text-xs transition-all active:scale-[0.98] ${addDraft.lostReason === 'FOUL' ? 'bg-error border-error text-white' : 'border-error/40 text-error hover:border-error'}`}
                      >
                        SERVE FOUL — opponent gets point
                      </button>
                      <div className="grid grid-cols-3 gap-2">
                        {LOST_REASONS.map(reason => (
                          <button key={reason}
                            onClick={() => setAddDraft(d => ({ ...d, lostReason: d.lostReason === reason ? '' : reason, pointWinnerId: '' }))}
                            className={`py-2.5 rounded-lg border font-bold text-xs transition-all active:scale-[0.98] ${addDraft.lostReason === reason ? 'bg-primary border-primary text-white' : 'bg-surface border-border text-text-secondary'}`}>
                            {LOST_REASON_LABELS[reason]}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Point winner — IN / TOUCH only */}
                    {addDraft.lostReason && LOST_REASON_REQUIRES_WINNER[addDraft.lostReason as LostReason] && (
                      <div>
                        <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Point winner player</p>
                        <PlayerSelects teamAPlayers={teamA.players} teamBPlayers={teamB.players}
                          value={addDraft.pointWinnerId} onChange={v => setAddDraft(d => ({ ...d, pointWinnerId: v }))} />
                      </div>
                    )}

                    {addError && <p className="text-sm text-error font-semibold">{addError}</p>}

                    <div className="flex gap-2">
                      <button onClick={handleAddRally} disabled={addSaving}
                        className="flex-1 py-3 rounded-lg font-bold text-sm bg-primary text-white hover:opacity-90 disabled:opacity-50 active:scale-[0.98] transition-all">
                        {addSaving ? 'Adding…' : 'Add Rally'}
                      </button>
                      <button onClick={() => { setShowAddForm(false); setAddError(null); setAddDraft(emptyDraft()); }} disabled={addSaving}
                        className="py-3 px-5 rounded-lg font-semibold text-sm border border-border text-text-secondary hover:bg-surface-raised active:scale-[0.98] transition-all">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
